/**
 * PriceChartWidget - Price chart with candlestick/area visualization
 *
 * Uses Highcharts Stock for rendering OHLCV data
 * Supports multiple tickers with layout options (tabs, merged, grid)
 * Includes stock info header with logo, ticker, price, and change
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import 'highcharts/modules/stock';
import { WIDGET_BACKGROUND } from './chartTheme';
import { callWidgetApi } from '../../utils/widgetApi';

// Time range configurations
const TIME_RANGES = [
    { label: '1D', value: '1D', days: 1, interval: '5m' },
    { label: '1M', value: '1M', days: 30, interval: '1h' },
    { label: '6M', value: '6M', days: 180, interval: '1d' },
    { label: 'YTD', value: 'YTD', days: null, interval: '1d' },
    { label: '1Y', value: '1Y', days: 365, interval: '1d' },
    { label: '5Y', value: '5Y', days: 1825, interval: '1d' },
];

/**
 * Format number with Vietnamese locale
 */
function formatNumber(value, options = {}) {
    if (value === null || value === undefined) return '-';

    const {
        decimals = 2,
        suffix = '',
        prefix = '',
        compact = false
    } = options;

    if (compact && Math.abs(value) >= 1e9) {
        return `${prefix}${(value / 1e9).toFixed(1)}B${suffix}`;
    }
    if (compact && Math.abs(value) >= 1e6) {
        return `${prefix}${(value / 1e6).toFixed(1)}M${suffix}`;
    }
    if (compact && Math.abs(value) >= 1e3) {
        return `${prefix}${(value / 1e3).toFixed(1)}K${suffix}`;
    }

    return `${prefix}${value.toLocaleString('vi-VN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    })}${suffix}`;
}

/**
 * Format price change with color
 */
function PriceChange({ value, percent }) {
    if (value === null || value === undefined) return null;

    const isPositive = value > 0;
    const isNegative = value < 0;

    const colorClass = isPositive
        ? 'text-green-600 dark:text-green-400'
        : isNegative
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-600 dark:text-gray-400';

    const sign = isPositive ? '+' : '';

    return (
        <span className={colorClass}>
            {sign}{formatNumber(value, { decimals: 0 })}
            {percent !== undefined && (
                <span className="ml-1">({sign}{formatNumber(percent, { decimals: 2 })}%)</span>
            )}
        </span>
    );
}

/**
 * Stock info header component
 */
function StockInfoHeader({ stockInfo, loading }) {
    if (loading) {
        return (
            <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
                    <div className="flex-1">
                        <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-1" />
                        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    </div>
                    <div className="text-right">
                        <div className="h-7 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-1" />
                        <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    </div>
                </div>
            </div>
        );
    }

    if (!stockInfo) return null;

    const {
        ticker,
        nameVi,
        nameEn,
        priceClose,
        netChange,
        pctChange,
        stockExchange,
        imageUrl
    } = stockInfo;

    const displayName = nameVi || nameEn || ticker;

    return (
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
                {/* Logo */}
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={ticker}
                        className="w-10 h-10 rounded-full object-contain bg-gray-100"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                        {ticker?.charAt(0) || '?'}
                    </div>
                )}

                {/* Name and Exchange */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-lg text-gray-900 dark:text-white">
                            {ticker}
                        </span>
                        {stockExchange && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300">
                                {stockExchange}
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {displayName}
                    </p>
                </div>

                {/* Price */}
                <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatNumber(priceClose, { decimals: 0 })}
                    </div>
                    <div className="text-sm">
                        <PriceChange value={netChange} percent={pctChange} />
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Tab selector for multi-ticker layout
 */
function TabSelector({ tickers, activeTab, onTabChange }) {
    return (
        <div className="flex border-b border-gray-200 dark:border-gray-700">
            {tickers.map((ticker) => (
                <button
                    key={ticker}
                    onClick={() => onTabChange(ticker)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === ticker
                        ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                    {ticker}
                </button>
            ))}
        </div>
    );
}

/**
 * Main PriceChartWidget component
 */
export default function PriceChartWidget({ data, config }) {
    const chartRef = useRef(null);
    const [activeTab, setActiveTab] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [timeRange, setTimeRange] = useState('1M'); // Default to 1M for comparison
    const [chartType, setChartType] = useState('candlestick'); // 'candlestick' or 'area'
    const [priceData, setPriceData] = useState(null);
    const [allTickersData, setAllTickersData] = useState({}); // For merged layout
    const [allTickersInfo, setAllTickersInfo] = useState({}); // Stock info for all tickers in merged mode
    const [loading, setLoading] = useState(false);
    const [stockInfo, setStockInfo] = useState(null);
    const [loadingStockInfo, setLoadingStockInfo] = useState(false);

    // Detect dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };
        checkDarkMode();

        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

        return () => observer.disconnect();
    }, []);

    // Check if data is array (single ticker direct data) or object (multi-ticker keyed by ticker)
    const isDirectArray = Array.isArray(data);

    // Get tickers from config
    const tickers = useMemo(() => {
        if (config?.params?.tickers) {
            const t = config.params.tickers;
            return Array.isArray(t) ? t : (typeof t === 'string' ? [t] : []);
        }
        if (config?.params?.ticker) {
            return [config.params.ticker];
        }
        // If data is array, we have single ticker - get from config or use default
        if (isDirectArray) {
            return ['Chart'];
        }
        // Data is object keyed by ticker
        return Object.keys(data || {});
    }, [config, data, isDirectArray]);

    // Set initial active tab
    useEffect(() => {
        if (tickers.length > 0 && !activeTab) {
            setActiveTab(tickers[0]);
        }
    }, [tickers, activeTab]);

    // Fetch stock info when ticker changes
    useEffect(() => {
        const ticker = activeTab || tickers[0];
        if (!ticker || ticker === 'Chart') {
            setStockInfo(null);
            return;
        }

        let cancelled = false;

        async function fetchStockInfo() {
            setLoadingStockInfo(true);
            try {
                const result = await callWidgetApi('get_ticker_info', { ticker });

                if (!cancelled) {
                    setStockInfo(result.data || result);
                }
            } catch (err) {
                console.warn('Failed to fetch stock info:', err);
                setStockInfo(null);
            } finally {
                if (!cancelled) {
                    setLoadingStockInfo(false);
                }
            }
        }

        fetchStockInfo();

        return () => {
            cancelled = true;
        };
    }, [activeTab, tickers]);

    // Get layout mode - default to 'merged' for multi-ticker comparison
    const layout = config?.layout || (tickers.length > 1 ? 'merged' : 'single');

    // Is merged mode (comparison chart)?
    const isMergedMode = layout === 'merged' && tickers.length > 1;

    // Get time params
    const getTimeParams = (range) => {
        const now = Math.floor(Date.now() / 1000);
        const rangeConfig = TIME_RANGES.find(r => r.value === range) || TIME_RANGES[1]; // Default 1M

        let from;
        if (rangeConfig.value === 'YTD') {
            from = Math.floor(Date.UTC(new Date().getUTCFullYear(), 0, 1) / 1000);
        } else {
            from = now - (rangeConfig.days * 24 * 60 * 60);
        }

        return { from, to: now, interval: rangeConfig.interval };
    };

    // Fetch ALL tickers data when in merged mode
    useEffect(() => {
        if (!isMergedMode || tickers.length === 0) return;

        let cancelled = false;

        async function fetchAllTickersData() {
            setLoading(true);
            try {
                const { from, to, interval } = getTimeParams(timeRange);

                // Fetch data for all tickers in a single API call
                const result = await callWidgetApi('get_historical_prices_range', {
                    tickers,
                    interval,
                    from_timestamp: from,
                    to_timestamp: to,
                    type: 'stock'
                });

                if (!cancelled) {
                    // Backend always returns {ticker: data} format
                    setAllTickersData(result.data);
                }
            } catch (err) {
                console.warn('Failed to fetch all tickers data:', err);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchAllTickersData();

        return () => {
            cancelled = true;
        };
    }, [isMergedMode, tickers, timeRange]);

    // Fetch stock info for all tickers in merged mode
    useEffect(() => {
        if (!isMergedMode || tickers.length === 0) return;

        let cancelled = false;

        async function fetchAllStockInfo() {
            setLoadingStockInfo(true);
            try {
                const results = await Promise.all(
                    tickers.map(async (ticker) => {
                        try {
                            const result = await callWidgetApi('get_ticker_info', { ticker });
                            return { ticker, data: result.data || result };
                        } catch (err) {
                            console.warn(`Failed to fetch info for ${ticker}:`, err);
                            return { ticker, data: null };
                        }
                    })
                );

                if (!cancelled) {
                    const infoByTicker = {};
                    results.forEach(({ ticker, data }) => {
                        infoByTicker[ticker] = data;
                    });
                    setAllTickersInfo(infoByTicker);
                }
            } catch (err) {
                console.warn('Failed to fetch all stock info:', err);
            } finally {
                if (!cancelled) {
                    setLoadingStockInfo(false);
                }
            }
        }

        fetchAllStockInfo();

        return () => {
            cancelled = true;
        };
    }, [isMergedMode, tickers]);

    // Fetch data when ticker or timeRange changes (single/tabs mode)
    useEffect(() => {
        // Skip if in merged mode - handled by separate effect
        if (isMergedMode) return;

        const ticker = activeTab || tickers[0];
        if (!ticker || ticker === 'Chart') {
            // Use provided data if no valid ticker
            if (isDirectArray) {
                setPriceData(data);
            } else if (data && activeTab) {
                setPriceData(data[activeTab]);
            }
            return;
        }

        let cancelled = false;

        async function fetchData() {
            setLoading(true);
            try {
                const { from, to, interval } = getTimeParams(timeRange);
                const result = await callWidgetApi('get_historical_prices_range', {
                    tickers: [ticker],
                    interval,
                    from_timestamp: from,
                    to_timestamp: to,
                    type: 'stock'
                });

                if (!cancelled) {
                    // Backend always returns {ticker: data} format
                    setPriceData(result.data[ticker] || []);
                }
            } catch (err) {
                console.warn('Failed to fetch price data:', err);
                // Fallback to provided data
                if (isDirectArray) {
                    setPriceData(data);
                } else if (data && activeTab) {
                    setPriceData(data[activeTab]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [isMergedMode, activeTab, timeRange, tickers, data, isDirectArray]);

    // Color palette for comparison chart
    const COMPARISON_COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899'];

    // Render merged/comparison chart
    useEffect(() => {
        if (!isMergedMode || !chartRef.current) return;

        // Check if we have data for at least one ticker
        const hasData = tickers.some(t => allTickersData[t]?.length > 0);
        if (!hasData) return;

        // Build percentage change series for each ticker
        const series = tickers.map((ticker, index) => {
            const tickerData = allTickersData[ticker] || [];
            if (tickerData.length === 0) return null;

            // Get first close price as baseline
            const firstItem = tickerData[0];
            const baseClose = Array.isArray(firstItem) ? firstItem[4] : firstItem.close;

            // Calculate percentage change from baseline
            const pctData = tickerData.map(item => {
                let ts, close;
                if (Array.isArray(item)) {
                    ts = item[0];
                    close = item[4];
                } else {
                    ts = item.timestamp;
                    close = item.close;
                }
                const timestamp = ts < 1e12 ? ts * 1000 : ts;
                const pctChange = ((close - baseClose) / baseClose) * 100;
                return [timestamp, pctChange];
            });

            return {
                type: 'line',
                name: ticker,
                data: pctData,
                color: COMPARISON_COLORS[index % COMPARISON_COLORS.length],
                lineWidth: 2,
                marker: { enabled: false }
            };
        }).filter(Boolean);

        if (series.length === 0) return;

        Highcharts.chart(chartRef.current, {
            chart: {
                height: 320,
                backgroundColor: 'transparent',
                spacing: [10, 10, 10, 10]
            },
            title: { text: null },
            credits: { enabled: false },
            exporting: { enabled: false },
            legend: { enabled: false },
            rangeSelector: { enabled: false },
            scrollbar: { enabled: false },
            navigator: {
                enabled: true,
                height: 40,
                maskFill: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                series: {
                    type: 'line',
                    color: '#3b82f6',
                    lineWidth: 1
                },
                xAxis: {
                    labels: {
                        style: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: '10px' }
                    }
                }
            },
            xAxis: {
                type: 'datetime',
                lineColor: isDarkMode ? '#374151' : '#e5e7eb',
                tickLength: 0,
                labels: {
                    style: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: '11px' }
                },
                crosshair: { color: isDarkMode ? '#4b5563' : '#d1d5db' }
            },
            yAxis: {
                title: { text: null },
                gridLineColor: isDarkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(0, 0, 0, 0.04)',
                labels: {
                    align: 'right',
                    x: -5,
                    style: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: '11px' },
                    formatter: function () { return this.value.toFixed(0) + '%'; }
                },
                opposite: false,
                plotLines: [{
                    value: 0,
                    color: isDarkMode ? '#4b5563' : '#d1d5db',
                    dashStyle: 'dash',
                    width: 1
                }]
            },
            tooltip: {
                backgroundColor: isDarkMode ? '#374151' : '#ffffff',
                borderColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                style: { color: isDarkMode ? '#e5e7eb' : '#374151' },
                shared: true,
                useHTML: true,
                formatter: function () {
                    const points = this.points;
                    let html = `<div style="font-size:11px;margin-bottom:5px">${Highcharts.dateFormat('%d/%m/%Y', this.x)}</div>`;
                    points.forEach(point => {
                        const sign = point.y >= 0 ? '+' : '';
                        const color = point.y >= 0 ? '#22c55e' : '#ef4444';
                        html += `<div style="padding:2px 0;display:flex;align-items:center;gap:6px">
                            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${point.color}"></span>
                            <b>${point.series.name}:</b>
                            <span style="color:${color}">${sign}${point.y.toFixed(2)}%</span>
                        </div>`;
                    });
                    return html;
                }
            },
            plotOptions: {
                line: {
                    states: { hover: { lineWidth: 3 } }
                }
            },
            series: series
        });
    }, [isMergedMode, allTickersData, tickers, isDarkMode]);

    // Render single ticker chart (candlestick/area)
    useEffect(() => {
        // Skip if in merged mode
        if (isMergedMode) return;
        if (!chartRef.current || !priceData || priceData.length === 0) return;

        const ticker = activeTab || tickers[0];

        // Extract OHLC and volume data
        const ohlcData = [];
        const volumeData = [];
        const closeData = [];

        priceData.forEach(item => {
            let ts, open, high, low, close, volume;
            if (Array.isArray(item)) {
                [ts, open, high, low, close, volume] = item;
            } else {
                ts = item.timestamp;
                open = item.open;
                high = item.high;
                low = item.low;
                close = item.close;
                volume = item.volume || item.totalVolume || 0;
            }
            const timestamp = ts < 1e12 ? ts * 1000 : ts;
            ohlcData.push([timestamp, open, high, low, close]);
            closeData.push([timestamp, close]);

            // Volume color based on price change
            const isUp = close >= open;
            volumeData.push({
                x: timestamp,
                y: volume || 0,
                color: isUp ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)'
            });
        });

        // Determine color for area chart
        const firstClose = closeData[0]?.[1] || 0;
        const lastClose = closeData[closeData.length - 1]?.[1] || 0;
        const isUp = lastClose >= firstClose;
        const lineColor = isUp ? '#22c55e' : '#ef4444';

        // Build series based on chart type
        const series = chartType === 'candlestick' ? [
            {
                type: 'candlestick',
                name: ticker,
                data: ohlcData,
                yAxis: 0
            },
            {
                type: 'column',
                name: 'Volume',
                data: volumeData,
                yAxis: 1
            }
        ] : [
            {
                type: 'area',
                name: ticker,
                data: closeData,
                yAxis: 0,
                color: lineColor,
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, isUp ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'],
                        [1, isUp ? 'rgba(34, 197, 94, 0.02)' : 'rgba(239, 68, 68, 0.02)']
                    ]
                },
                lineWidth: 2,
                threshold: null
            },
            {
                type: 'column',
                name: 'Volume',
                data: volumeData,
                yAxis: 1
            }
        ];

        Highcharts.chart(chartRef.current, {
            chart: {
                height: 320,
                backgroundColor: 'transparent',
                spacing: [10, 10, 10, 10]
            },
            title: { text: null },
            credits: { enabled: false },
            exporting: { enabled: false },
            legend: { enabled: false },
            rangeSelector: { enabled: false },
            scrollbar: { enabled: false },
            navigator: {
                enabled: true,
                height: 40,
                maskFill: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                series: {
                    type: 'area',
                    color: '#3b82f6',
                    fillOpacity: 0.1,
                    lineWidth: 1
                },
                xAxis: {
                    labels: {
                        style: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: '10px' }
                    }
                }
            },
            xAxis: {
                type: 'datetime',
                lineColor: isDarkMode ? '#374151' : '#e5e7eb',
                tickLength: 0,
                labels: {
                    style: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: '11px' }
                },
                crosshair: { color: isDarkMode ? '#4b5563' : '#d1d5db' }
            },
            yAxis: [{
                // Price axis
                title: { text: null },
                height: '75%',
                gridLineColor: isDarkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(0, 0, 0, 0.04)',
                labels: {
                    align: 'right',
                    x: -5,
                    style: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: '11px' },
                    formatter: function () { return formatNumber(this.value, { decimals: 0 }); }
                },
                opposite: false
            }, {
                // Volume axis
                title: { text: null },
                top: '78%',
                height: '22%',
                offset: 0,
                gridLineWidth: 0,
                labels: { enabled: false }
            }],
            tooltip: {
                backgroundColor: isDarkMode ? '#374151' : '#ffffff',
                borderColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                style: { color: isDarkMode ? '#e5e7eb' : '#374151' },
                shared: true,
                useHTML: true,
                formatter: function () {
                    const points = this.points;
                    let html = `<div style="font-size:11px;margin-bottom:5px">${Highcharts.dateFormat('%d/%m/%Y', this.x)}</div>`;
                    points.forEach(point => {
                        if (point.series.type === 'candlestick') {
                            html += `<div style="padding:2px 0"><b>O:</b> ${formatNumber(point.point.open, { decimals: 0 })} <b>H:</b> ${formatNumber(point.point.high, { decimals: 0 })} <b>L:</b> ${formatNumber(point.point.low, { decimals: 0 })} <b>C:</b> ${formatNumber(point.point.close, { decimals: 0 })}</div>`;
                        } else if (point.series.type === 'area') {
                            html += `<div style="padding:2px 0"><b>Giá:</b> ${formatNumber(point.y, { decimals: 0 })}</div>`;
                        } else if (point.series.type === 'column') {
                            html += `<div style="padding:2px 0"><b>KL:</b> ${formatNumber(point.y, { compact: true })}</div>`;
                        }
                    });
                    return html;
                }
            },
            plotOptions: {
                candlestick: {
                    color: '#ef4444',
                    upColor: '#22c55e',
                    lineColor: '#ef4444',
                    upLineColor: '#22c55e'
                },
                column: {
                    borderWidth: 0
                },
                area: {
                    marker: { enabled: false },
                    states: { hover: { lineWidth: 2 } }
                }
            },
            series: series
        });
    }, [isMergedMode, priceData, activeTab, tickers, isDarkMode, chartType]);

    if (!data && tickers.length === 0) {
        return (
            <div className="border border-gray-100 dark:border-gray-800 p-4 rounded-xl" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                <p className="text-gray-500">No chart data available</p>
            </div>
        );
    }

    // Render merged mode (comparison chart)
    if (isMergedMode) {
        return (
            <div className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                {/* Ticker badges with prices */}
                <div className="flex flex-wrap items-center gap-2 p-3 border-b border-gray-100 dark:border-gray-700">
                    {tickers.map((ticker, index) => {
                        const info = allTickersInfo[ticker];
                        const color = COMPARISON_COLORS[index % COMPARISON_COLORS.length];
                        const pctChange = info?.pctChange;
                        const isPositive = pctChange > 0;
                        const isNegative = pctChange < 0;

                        return (
                            <div
                                key={ticker}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-600"
                            >
                                {/* Color dot */}
                                <span
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: color }}
                                />

                                {/* Logo */}
                                {info?.imageUrl && (
                                    <img
                                        src={info.imageUrl}
                                        alt={ticker}
                                        className="w-5 h-5 rounded-full object-contain"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                )}

                                {/* Ticker */}
                                <span className="font-medium text-sm text-gray-900 dark:text-white">
                                    {ticker}
                                </span>

                                {/* Price */}
                                {info?.priceClose && (
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                        {formatNumber(info.priceClose, { decimals: 0 })}
                                    </span>
                                )}

                                {/* Change % */}
                                {pctChange !== undefined && (
                                    <span className={`text-sm font-medium ${
                                        isPositive ? 'text-green-600 dark:text-green-400' :
                                        isNegative ? 'text-red-600 dark:text-red-400' :
                                        'text-gray-500'
                                    }`}>
                                        {isPositive ? '+' : ''}{formatNumber(pctChange, { decimals: 2 })}%
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Time range selector */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-1">
                        {TIME_RANGES.map(({ label, value }) => (
                            <button
                                key={value}
                                onClick={() => setTimeRange(value)}
                                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${timeRange === value
                                    ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Chart */}
                <div className="px-2 py-2">
                    {loading ? (
                        <div className="h-[320px] animate-pulse bg-gray-100 dark:bg-gray-700 rounded" />
                    ) : (
                        <div ref={chartRef} className="w-full" style={{ height: '320px' }} />
                    )}
                </div>

                {/* Stock info list */}
                <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                    {tickers.map((ticker, index) => {
                        const info = allTickersInfo[ticker];
                        const color = COMPARISON_COLORS[index % COMPARISON_COLORS.length];
                        if (!info) return null;

                        const pctChange = info.pctChange;
                        const netChange = info.netChange;
                        const isPositive = pctChange > 0;
                        const isNegative = pctChange < 0;

                        return (
                            <div key={ticker} className="flex items-center gap-3 px-4 py-3">
                                {/* Color indicator */}
                                <div
                                    className="w-1 h-10 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: color }}
                                />

                                {/* Logo */}
                                {info.imageUrl ? (
                                    <img
                                        src={info.imageUrl}
                                        alt={ticker}
                                        className="w-9 h-9 rounded-full object-contain bg-gray-100"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <div
                                        className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
                                        style={{ backgroundColor: color }}
                                    >
                                        {ticker.charAt(0)}
                                    </div>
                                )}

                                {/* Name */}
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {info.nameVi || info.nameEn || ticker}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {ticker} · {info.stockExchange || 'HOSE'}
                                    </div>
                                </div>

                                {/* Price & Change */}
                                <div className="text-right">
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                        {formatNumber(info.priceClose, { decimals: 0 })}
                                    </div>
                                    <div className={`text-sm ${
                                        isPositive ? 'text-green-600 dark:text-green-400' :
                                        isNegative ? 'text-red-600 dark:text-red-400' :
                                        'text-gray-500'
                                    }`}>
                                        {isPositive ? '+' : ''}{formatNumber(netChange, { decimals: 0 })}
                                        <span className="ml-1">
                                            {isPositive ? '▲' : isNegative ? '▼' : ''} {Math.abs(pctChange || 0).toFixed(2)}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        Dữ liệu từ Simplize
                    </span>
                </div>
            </div>
        );
    }

    // Render single/tabs mode
    return (
        <div className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
            {/* Tab selector for multi-ticker */}
            {layout === 'tabs' && tickers.length > 1 && (
                <TabSelector
                    tickers={tickers}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />
            )}

            {/* Stock info header */}
            <StockInfoHeader
                stockInfo={stockInfo}
                loading={loadingStockInfo}
            />

            {/* Time range selector + Chart type toggle */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                {/* Time ranges */}
                <div className="flex items-center gap-1">
                    {TIME_RANGES.map(({ label, value }) => (
                        <button
                            key={value}
                            onClick={() => setTimeRange(value)}
                            className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${timeRange === value
                                ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Chart type toggle */}
                <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-600 rounded p-0.5">
                    <button
                        onClick={() => setChartType('candlestick')}
                        className={`p-1.5 rounded transition-colors ${chartType === 'candlestick'
                            ? 'bg-gray-100 dark:bg-gray-700'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        title="Biểu đồ nến"
                    >
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="4" y="6" width="3" height="12" rx="0.5" />
                            <rect x="10.5" y="3" width="3" height="18" rx="0.5" />
                            <rect x="17" y="8" width="3" height="8" rx="0.5" />
                        </svg>
                    </button>
                    <button
                        onClick={() => setChartType('area')}
                        className={`p-1.5 rounded transition-colors ${chartType === 'area'
                            ? 'bg-gray-100 dark:bg-gray-700'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        title="Biểu đồ vùng"
                    >
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4v16" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Chart */}
            <div className="px-2 py-2">
                {loading ? (
                    <div className="h-[320px] animate-pulse bg-gray-100 dark:bg-gray-700 rounded" />
                ) : (
                    <div ref={chartRef} className="w-full" style={{ height: '320px' }} />
                )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    Dữ liệu từ Simplize
                </span>
                <a
                    href={`https://simplize.vn/co-phieu/${activeTab || tickers[0]}/ky-thuat`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                    Phân tích kỹ thuật
                </a>
            </div>
        </div>
    );
}
